/**
 * 메일 API — backend /mail/* 와 1:1 (v2.108.0)
 *
 * 문서 첨부는 { docType, docId } 좌표만 보낸다 — 파일 내용은 서버가 만든다.
 * 로컬 파일만 multipart 로 실어 보낸다.
 */

import type { ApiResponse } from './types';
import type { RichDoc, RichNode } from './rich-doc';
import { api } from './api';
import { buildCompanyBlock } from './mail-signature-template';

const BASE = '/api';

/** 서버 lib/mail-send/doc-source.ts 의 MAIL_DOC_TYPES 와 같은 목록이어야 한다 */
export type MailDocType =
  | 'quote'
  | 'quote-xlsx'
  | 'po'
  | 'tech-support'
  | 'tech-support-docx'
  | 'trade-statement'
  | 'delivery-confirmation'
  | 'nameplate'
  | 'delivery'
  | 'commencement'
  | 'bell-status'
  | 'attachment'
  /** NAS 파일 (v2.313.0) — params = { path, name } · 서버가 보내는 사람 본인 NAS 계정으로 가져온다 */
  | 'nas-file';

/**
 * 영업 권한(sales·mgmt·admin)만 첨부할 수 있는 문서 종류 (v2.250.0 · viewer 에게 메일을 열며).
 *   ⭐서버 lib/mail-send/doc-source.ts `MAIL_DOC_MIN_ROLE` 의 'sales' 줄과 **같은 목록** — verify:mail 이 둘을 대조한다.
 *   화면은 viewer 에게 이 문서들의 [✉ 메일] 단추를 안 그린다(MailSendButton). 서버도 막으므로 화면은 「못 쓰는 단추를 안 보이기」만.
 *   (보관 첨부 'attachment' 는 종류가 아니라 한 건마다 서버가 판정한다 — 기밀 종류는 viewer 목록에 애초에 안 나온다)
 */
export const MAIL_DOC_SALES_ONLY: ReadonlySet<MailDocType> = new Set<MailDocType>([
  'quote', 'quote-xlsx', 'po', 'trade-statement', 'bell-status',
]);

export interface MailDocRef {
  docType: MailDocType;
  docId: string | number;
  params?: unknown;
  /** 화면 표시용 이름 (서버가 실제 파일명을 다시 정한다) */
  label?: string;
  /** 알고 있으면 표시용 크기 (보관 첨부 등) */
  sizeHint?: number;
}

export interface MailAccountStatus {
  configured: boolean;
  connected: boolean;
  status: 'ACTIVE' | 'EXPIRED' | null;
  nwEmail: string | null;
  connectedAt: string | null;
  refreshIssuedAt: string | null;
  lastRefreshError: string | null;
  /**
   * 참조에 **항상** 들어가는 영업 공용 메일 (v2.176.0).
   * ⭐주소는 서버가 정한다 — 여기에 값을 적어 두지 말 것(바꿀 때 한쪽만 바뀐다).
   */
  alwaysCc?: string | null;
}

export function getMailAccount(): Promise<MailAccountStatus> {
  return api.get<MailAccountStatus>('/mail/account');
}

export function connectMailAccount(): Promise<{ authorizeUrl: string }> {
  return api.post<{ authorizeUrl: string }>('/mail/account/connect');
}

export function disconnectMailAccount(): Promise<{ disconnected: boolean }> {
  return api.delete<{ disconnected: boolean }>('/mail/account');
}

export interface RecipientSuggestion {
  name: string;
  email: string;
  /**
   * 소속 (v2.195.0 · 사용자 요청 「검색한 목록 나올 때 소속회사도 같이 보이게」).
   * 담당자=거래처 이름 · 직원=부서 · 거래처=빈 글자(이름이 곧 회사) · 웍스=응답에 있으면.
   */
  company: string;
  source: 'works' | 'contact' | 'partner' | 'employee';
}

export function searchRecipients(
  q: string,
): Promise<{ suggestions: RecipientSuggestion[]; worksAvailable: boolean }> {
  return api.get(`/mail/recipients?q=${encodeURIComponent(q)}`);
}

export interface MailSendLogRow {
  id: number;
  nw_email: string | null;
  to_addrs: string;
  cc_addrs: string | null;
  subject: string;
  attachments: Array<{ source: string; filename: string; size: number }>;
  total_bytes: number;
  status: 'sent' | 'failed';
  error: string | null;
  created_at: string;
}

export function listMailLogs(): Promise<{ logs: MailSendLogRow[] }> {
  return api.get('/mail/logs');
}

export interface MailSendResult {
  logId: number | null;
  to: string[];
  cc: string[];
  /** 사람이 붙인 첨부만 (본문 그림은 여기 안 센다 — v2.125.0) */
  attachmentCount: number;
  /** 본문에 박혀 나간 그림 수 (붙여넣은 그림 + 꼬리말 로고) */
  inlineCount: number;
  totalBytes: number;
}

/** 발송 — payload JSON + 로컬 파일 multipart. 오류는 Error(message) 로 던진다. */
/**
 * 답장·전달 (v2.205.0 · 메일 메뉴) — 서버에는 「어느 메일을 어떻게」만 보낸다.
 * 원문 인용은 서버가 원문을 다시 받아 짓는다(화면이 보낸 원문을 믿지 않는다).
 */
export interface MailQuote {
  mailId: number;
  mode: 'reply' | 'replyAll' | 'forward';
  /** 전달할 원문 첨부 번호(0 이상) — 전달일 때만 쓴다 */
  attachmentIds?: number[];
}

export async function sendMail(input: {
  to: string[];
  cc: string[];
  subject: string;
  /** 글자만 — 발송 기록(body_text)용. 서버도 문서에서 같은 규칙으로 다시 만든다 */
  body: string;
  /**
   * 서식 있는 본문 (v2.124.0) — 편집기의 문서 나무. 새 그림은 `dataUrl` 로 실려 가고
   * 서버가 본문 안 그림(cid 첨부)으로 바꿔 보낸다. HTML 은 서버가 만든다.
   */
  bodyDoc: RichDoc;
  docRefs: MailDocRef[];
  files: File[];
  quote?: MailQuote;
  /**
   * 영업 공용 메일(sales@)을 참조에 붙이나 (v2.250.0) — false = 쓰기 창에서 × 로 뺐다.
   *   ⭐서버가 붙이는 주소라 **이 칸을 보내야** 실제로 빠진다(서버는 칸이 없으면 붙인다 — 옛 화면 호환 · ccForSend).
   */
  salesCc?: boolean;
}): Promise<MailSendResult> {
  const fd = new FormData();
  fd.append(
    'payload',
    JSON.stringify({
      to: input.to,
      cc: input.cc,
      salesCc: input.salesCc,
      subject: input.subject,
      body: input.body,
      bodyDoc: input.bodyDoc,
      // 화면 표시용 필드는 벗겨 보낸다 (서버 스키마와 일치 · 이름 보기와 같은 docRefWire)
      docRefs: input.docRefs.map(docRefWire),
      ...(input.quote
        ? { quote: { mailId: input.quote.mailId, mode: input.quote.mode, attachmentIds: input.quote.attachmentIds ?? [] } }
        : {}),
    }),
  );
  for (const f of input.files) fd.append('file', f);

  const res = await fetch(`${BASE}/mail/send`, {
    method: 'POST',
    credentials: 'include',
    body: fd,
  });
  const json = (await res.json()) as ApiResponse<MailSendResult>;
  if (!json.ok) {
    const code = (json.error.details as { code?: string } | undefined)?.code;
    const err = new Error(json.error.message) as Error & { mailCode?: string };
    if (code) err.mailCode = code;
    throw err;
  }
  return json.data;
}

/**
 * 첨부 파일 이름 — 서버가 `Content-Disposition` 에 적어 준 것을 그대로 쓴다 (v2.123.0).
 *
 * ⭐화면에 보이는 첨부 이름(label)은 **사람이 붙인 표시용 이름표**라 실제 파일 이름이 아니다.
 *   저장되는 이름을 이름표로 정하면 「받아 보니 다른 이름」이 된다 — 서버가 정한 이름을 쓴다.
 * ⚠한글 이름은 `filename*=UTF-8''…` 쪽에만 온전히 들어 있다. 따옴표 안의 것은 ASCII 사본이다.
 */
function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null;
  const star = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (star?.[1]) {
    try { return decodeURIComponent(star[1].trim()); } catch { /* 아래 사본으로 */ }
  }
  const plain = /filename="([^"]*)"/i.exec(header);
  return plain?.[1] ?? null;
}

/**
 * 보내기 전에 첨부를 내려받는다 (v2.123.0 · 사용자 요청).
 *
 * ⭐**발송과 같은 통로**(`POST /mail/attachment` → 서버의 resolveMailDoc)를 탄다 —
 *   문서 종류별 옛 다운로드 주소를 화면이 직접 부르면 「무엇을 어디서 받나」가 두 벌이 되고,
 *   언젠가 갈려서 「확인한 파일과 나간 파일이 다른」 모양이 된다.
 * ⚠plain `<a href>` 로 열면 오류(권한·세션)일 때 JSON 페이지로 이탈한다 — fetch+blob 으로 받는다.
 */
export async function downloadMailAttachment(ref: MailDocRef): Promise<void> {
  const res = await fetch(`${BASE}/mail/attachment`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ docRef: ref }),
  });
  if (!res.ok) {
    let msg = `첨부를 받지 못했습니다 (${res.status})`;
    try {
      const j = (await res.json()) as ApiResponse<unknown>;
      if (!j.ok) msg = j.error.message;
    } catch { /* 본문이 JSON 이 아니면 기본 문구 */ }
    throw new Error(msg);
  }
  const blob = await res.blob();
  const name = filenameFromDisposition(res.headers.get('content-disposition'))
    ?? (ref.label || '첨부파일');
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // ⭐반드시 해제한다 — 안 하면 페이지를 떠날 때까지 파일 하나가 통째로 메모리에 남는다
    URL.revokeObjectURL(url);
  }
}

/** 서버로 보낼 첨부 좌표만 — 화면 표시용 필드(label·sizeHint)는 벗긴다 (발송·이름 보기 공용) */
export function docRefWire(d: MailDocRef): { docType: MailDocType; docId: string | number; params?: unknown } {
  return { docType: d.docType, docId: d.docId, ...(d.params !== undefined ? { params: d.params } : {}) };
}

/**
 * 「발송 시 생성」 첨부가 **실제로 붙을 파일 이름** (v2.240.0 · 사용자 요청).
 *   서버가 종류별 생성 함수와 같은 이름 규칙으로 정한다(파일은 만들지 않는다) · 겹치는 이름은 보낼 때처럼 「(2)」.
 *   줄마다 filename 또는 error — 실패한 줄은 화면이 원래 이름표로 둔다.
 */
export async function getMailAttachmentNames(
  refs: MailDocRef[],
): Promise<Array<{ filename: string | null; error: string | null }>> {
  const res = await fetch(`${BASE}/mail/attachment-names`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ docRefs: refs.map(docRefWire) }),
  });
  const json = (await res.json()) as ApiResponse<{ names: Array<{ filename: string | null; error: string | null }> }>;
  if (!json.ok) throw new Error(json.error.message);
  return json.data.names;
}

/** 내 PC 에서 고른 파일은 서버를 안 거친다 — 브라우저가 들고 있는 것을 그대로 저장 */
export function downloadLocalFile(file: File): void {
  const url = URL.createObjectURL(file);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

/* ───────────── 메일 꼬리말 (v2.125.0) ─────────────
 * 마이페이지에 등록해 **보내는 메일마다** 본문 맨 아래에 따라 나가는 덩어리.
 * ⛔결재용 사인·직인(auth-api 의 mySignature)과 다른 것이다 — 그쪽은 그림 한 장이고
 *   판매보고서 결재판에 찍힌다.
 */

/**
 * 회사 정보 표에 들어갈 **내 직원 정보** (v2.211.0) — 서버가 준다.
 * 이름·직급·부서·휴대폰은 직원 정보(관리자가 사용자 관리에서 고친다), 메일은 연결된 웍스 계정.
 * 빈 값은 null — 표에서 그 조각이 통째로 빠진다(휴대폰이 없으면 M 줄이 없다).
 */
export interface MailSignatureProfile {
  name: string;
  title: string | null;
  dept: string | null;
  mobile: string | null;
  email: string | null;
}

/** 서버가 주는 꼬리말 — `doc` 은 **개인 부분(인사말 등)만**이다 (v2.211.0) */
export interface MailSignature {
  doc: RichDoc;
  /** 꺼 두면 저장은 남고 메일 창에 안 붙는다 (인사말·회사 정보 표 둘 다) */
  enabled: boolean;
  updatedAt: string | null;
  /** ⚠옛 서버(배포 중 잠깐)는 안 준다 — 그때는 회사 정보 표를 짓지 않는다 */
  profile?: MailSignatureProfile;
}

/**
 * 화면이 쓰는 꼬리말 = 서버 값 + **회사 정보 표(공통)** — 조회할 때 내 직원 정보로 지어 둔다.
 * ⭐메일 창은 열리는 순간 캐시에서 **동기로** 꺼내 붙인다(늦게 툭 튀어나오지 않게) — 그래서 표를
 *   조회 함수 안에서 미리 지어 캐시에 넣는다(표 그림 14KB 는 이때 한 번 받아 온다).
 */
export interface MailSignatureView extends MailSignature {
  companyBlock: RichNode[];
}

export async function getMailSignature(): Promise<MailSignatureView> {
  const sig = await api.get<MailSignature>('/mail/signature');
  const companyBlock = sig.profile ? await buildCompanyBlock(sig.profile) : [];
  return { ...sig, companyBlock };
}

export function saveMailSignature(doc: RichDoc, enabled: boolean): Promise<MailSignature> {
  return api.put<MailSignature>('/mail/signature', { doc, enabled });
}

export function deleteMailSignature(): Promise<{ deleted: boolean }> {
  return api.delete<{ deleted: boolean }>('/mail/signature');
}

/** 꼬리말 안 그림 주소 — 본인 것만 열린다 (서버가 user_id 로 거른다) */
export function mailSignatureImageUrl(imageId: number): string {
  return `${BASE}/mail/signature/image/${imageId}`;
}

/* ───────────── 받은 메일 읽기 (메일 메뉴 · v2.205.0) ─────────────
 * 서버가 웍스에서 그때그때 받아 넘긴다(우리 DB 에 메일을 두지 않는다).
 * 본문 html 은 서버가 이미 정리한 것이지만, 화면도 한 번 더 정리하고(DOMPurify) 격리 틀에만 그린다
 * — lib/mail-render.ts.
 */

export interface MailAddress { name: string | null; email: string }

export interface MailFolder {
  folderId: number;
  folderType: string;
  folderName: string;
  unreadMailCount: number;
  mailCount: number;
  folderDepth: number;
  parentFolderId: number | null;
}

export interface MailSummary {
  mailId: number;
  folderId: number | null;
  unread: boolean;
  from: MailAddress | null;
  to: MailAddress[];
  subject: string;
  receivedTime: string | null;
  sentTime: string | null;
  size: number;
  isImportant: boolean;
  attachCount: number;
}

export interface MailListPage {
  mails: MailSummary[];
  nextCursor: string | null;
  totalCount: number;
  unreadCount: number;
  folderName: string | null;
}

export interface MailAttachmentMeta {
  attachmentId: number;
  filename: string;
  size: number;
  contentType: string | null;
}

export interface MailMessage {
  mail: {
    mailId: number;
    folderId: number | null;
    subject: string;
    from: MailAddress | null;
    replyTo: MailAddress | null;
    to: MailAddress[];
    cc: MailAddress[];
    receivedTime: string | null;
    sentTime: string | null;
  };
  /** 서버가 정리한 본문 — 그대로 innerHTML 에 넣지 말 것(격리 틀 전용 · lib/mail-render.ts) */
  html: string;
  /** external = 바깥 그림을 보이게 둔 수 (images=1 일 때 · v2.210.0 · 옛 서버는 안 준다) */
  images: { embedded: number; omitted: number; blocked: number; external?: number };
  attachments: MailAttachmentMeta[];
  bigFiles: { filename: string; size: number; expireTime: string | null }[];
}

export function listMailFolders(): Promise<{ folders: MailFolder[]; nwEmail: string | null }> {
  return api.get('/mail/folders');
}

export function listMails(folderId: number, cursor: string | null, count = 50): Promise<MailListPage> {
  const qs = new URLSearchParams({ count: String(count) });
  if (cursor) qs.set('cursor', cursor);
  return api.get(`/mail/folders/${folderId}/mails?${qs}`);
}

/* ── 🔍 메일 검색 (v2.206.0) ───────────────────────────────────────────── */

/** 찾은 글자 강조 — 서버가 웍스의 `<mark>` 를 **글자 조각**으로 바꿔 준다. ⛔HTML 로 그리지 말 것(조각마다 글자로) */
export interface MailTextPart { t: string; hit: boolean }

/** 「내용으로 찾기」 결과 한 통 — 목록 줄(MailSummary)과 같은 칸 + 강조 조각 */
export interface MailSearchHit extends MailSummary {
  subjectParts: MailTextPart[];
  /** 본문 미리보기 한 줄 — 찾은 글자 둘레 */
  snippetParts: MailTextPart[];
  /** 이름에 찾은 글자가 든 첨부 (최대 3) */
  attachHits: MailTextPart[][];
}

export interface MailSearchPage {
  mails: MailSearchHit[];
  nextCursor: string | null;
  /** 찾은 기간 — years 1 이면 웍스 기본(최근 1년), 5 면 since 부터 */
  range: { years: 1 | 5; since: string | null };
}

/** 🔍 내용으로 찾기 — 웍스 공식 검색(제목·본문·첨부). folderId -1 = 전체 메일함 */
export function searchMails(p: { q: string; folderId: number; years: 1 | 5; cursor: string | null }): Promise<MailSearchPage> {
  const qs = new URLSearchParams({ q: p.q, folderId: String(p.folderId), years: String(p.years) });
  if (p.cursor) qs.set('cursor', p.cursor);
  return api.get(`/mail/search?${qs}`);
}

export interface MailSenderScanPage {
  mails: MailSummary[];
  /** 이번에 훑은 통 수 (한 번에 최대 1,000) */
  scanned: number;
  nextCursor: string | null;
  /** 메일함 끝까지 다 봤나 */
  done: boolean;
  /** 메일함 전체 통 수 (웍스가 알려 준 값) */
  totalCount: number | null;
}

/** 🔍 보낸 사람으로 찾기 — 지금 메일함을 최근 것부터 1,000통씩 훑는다(보낸메일함은 by='to') */
export function searchMailsBySender(folderId: number, p: { q: string; by: 'from' | 'to'; cursor: string | null }): Promise<MailSenderScanPage> {
  const qs = new URLSearchParams({ q: p.q, by: p.by });
  if (p.cursor) qs.set('cursor', p.cursor);
  return api.get(`/mail/folders/${folderId}/search-from?${qs}`);
}

/**
 * markRead — 목록에서 「안 읽음」이던 메일을 열 때만 true (서버가 웍스에 읽음 표시를 보낸다)
 * images — 바깥 그림(서명 로고 등)을 보이게 (v2.210.0 · 스팸메일함은 끄고 [그림 보기]로만)
 */
export function getMailMessage(mailId: number, markRead: boolean, images = false): Promise<MailMessage> {
  const qs = new URLSearchParams();
  if (markRead) qs.set('markRead', '1');
  if (images) qs.set('images', '1');
  const s = qs.toString();
  return api.get(`/mail/messages/${mailId}${s ? `?${s}` : ''}`);
}

/** 첨부 내려받기 주소 — 같은 주소(쿠키)라 링크로 열면 브라우저가 저장한다(서버가 attachment 로 내린다) */
export function mailAttachmentUrl(mailId: number, attachmentId: number): string {
  return `${BASE}/mail/messages/${mailId}/attachments/${attachmentId}`;
}

/**
 * 받은 메일의 첨부 내려받기 — fetch + blob (링크로 열면 오류일 때 JSON 쪽으로 이탈한다 · 위 downloadMailAttachment 와 같은 이유).
 * 저장 이름은 서버가 정리해 헤더에 적은 것을 쓴다(보낸 사람이 정한 이름에 경로 조각이 섞여 올 수 있다).
 */
export async function downloadInboxAttachment(mailId: number, attachmentId: number, fallbackName: string): Promise<void> {
  return saveFromUrl(mailAttachmentUrl(mailId, attachmentId), fallbackName);
}

/**
 * 📦 첨부 모두 저장 — 서버가 zip 하나로 묶어 준다 (v2.209.0).
 * ⚠화면에서 파일을 연달아 받지 않는다 — 크롬이 「여러 파일 다운로드」를 막으면 둘째부터 조용히 안 받아진다.
 */
export async function downloadInboxAttachmentsZip(mailId: number, fallbackName: string): Promise<void> {
  return saveFromUrl(`${BASE}/mail/messages/${mailId}/attachments/all`, fallbackName);
}

async function saveFromUrl(src: string, fallbackName: string): Promise<void> {
  const res = await fetch(src, { credentials: 'include' });
  if (!res.ok) {
    let msg = `첨부를 받지 못했습니다 (${res.status})`;
    try {
      const j = (await res.json()) as ApiResponse<unknown>;
      if (!j.ok) msg = j.error.message;
    } catch { /* 본문이 JSON 이 아니면 기본 문구 */ }
    throw new Error(msg);
  }
  const blob = await res.blob();
  const name = filenameFromDisposition(res.headers.get('content-disposition')) ?? fallbackName;
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}
